import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { FileUploadZone } from '../components/lead/FileUploadZone'
import { returnUploadApi } from '../lib/api'

function formatTimeRemaining(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now()
  if (diff <= 0) return 'Expired'

  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  if (hours > 24) return `${Math.floor(hours / 24)} days, ${hours % 24} hours remaining`
  if (hours > 0) return `${hours} hours, ${minutes} minutes remaining`
  return `${minutes} minutes remaining`
}

function labelForDocumentType(documentType: string): string {
  if (documentType === 'client_returned_docs') return 'Supporting documents'
  return documentType.replace(/_/g, ' ')
}

export function FiservReturnUploadPage() {
  const { token } = useParams<{ token: string }>()
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['fiserv-return-upload', token],
    queryFn: () => returnUploadApi.getUploadInfo(token!),
    enabled: !!token,
    retry: false,
  })

  const upload = useMutation({
    mutationFn: (file: File) => {
      if (!token) throw new Error('Missing upload token')
      return returnUploadApi.upload(token, file)
    },
    onSuccess: (_data, file) => {
      setUploadedFileName(file.name)
    },
  })

  if (isLoading) {
    return (
      <PageShell>
        <div className="py-12 text-center text-gray-500">Loading...</div>
      </PageShell>
    )
  }

  if (error || !data) {
    return (
      <PageShell>
        <div className="py-12 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
            <svg className="h-8 w-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="mb-2 text-lg font-semibold text-gray-900">Link Expired or Invalid</h1>
          <p className="text-sm text-gray-500">
            This upload link has expired or is no longer valid.<br />
            Please contact your AGMS representative for a new link.
          </p>
        </div>
      </PageShell>
    )
  }

  const maxSizeMb = data.max_file_size_mb ?? 25
  const accept = data.allowed_types || 'application/pdf,image/jpeg,image/png'

  return (
    <PageShell>
      <div className="mx-auto max-w-xl">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900">Document Upload</h1>
          <p className="mt-1 text-sm text-gray-600">
            AGMS has requested {labelForDocumentType(data.document_type).toLowerCase()} to continue your application.
          </p>
        </div>

        <div className="mb-6 flex items-center gap-2 text-sm text-gray-500">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {formatTimeRemaining(data.expires_at)}
        </div>

        {uploadedFileName ? (
          <div className="rounded-lg border border-green-200 bg-green-50 p-6 text-center">
            <svg className="mx-auto mb-3 h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-lg font-semibold text-green-800">Upload received</h2>
            <p className="mt-1 text-sm text-green-700">
              {uploadedFileName} was uploaded successfully. Your AGMS representative can now review it.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-2 text-sm font-semibold text-gray-700">
              {labelForDocumentType(data.document_type)}
            </h2>
            <FileUploadZone
              onFileSelect={(file) => upload.mutate(file)}
              accept={accept}
              maxSizeMb={maxSizeMb}
              isUploading={upload.isPending}
            />
            {upload.isError && (
              <p className="mt-2 text-xs text-red-600">
                Upload failed. Confirm the file type and size, then try again.
              </p>
            )}
            <p className="mt-2 text-xs text-gray-400">
              Max {maxSizeMb}MB. Accepted file types: {accept.replace(/,/g, ', ')}.
            </p>
          </div>
        )}
      </div>
    </PageShell>
  )
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-[#1e3a5f] px-6 py-4">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <div className="text-lg font-bold text-white">AGMS</div>
          <span className="text-sm text-blue-200">Merchant Document Upload</span>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-8">
        {children}
      </main>
    </div>
  )
}

export default FiservReturnUploadPage
